import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sunrise, UtensilsCrossed, Gift, ChefHat, Mountain, MapPin, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import ProductCard from "@/components/ProductCard";
import { ProductGridSkeleton } from "@/components/Skeletons";

const OCCASIONS = [
  { key: "breakfast", ar: "فطور", en: "Breakfast", icon: Sunrise, match: (p) => ["olive_oil", "zaatar_spices", "honey_jams", "olives_pickles"].includes(p.category) },
  { key: "hosting", ar: "ضيافة", en: "Hosting", icon: UtensilsCrossed, match: (p) => ["honey_jams", "olives_pickles", "zaatar_spices"].includes(p.category) },
  { key: "gift", ar: "هدية", en: "Gift", icon: Gift, match: (p) => ["soap_care", "honey_jams", "olive_oil"].includes(p.category) },
  { key: "cooking", ar: "طبخ", en: "Cooking", icon: ChefHat, match: (p) => ["olive_oil", "zaatar_spices", "olives_pickles"].includes(p.category) },
  { key: "nablus", ar: "من جبال نابلس", en: "From Nablus Mountains", icon: Mountain, match: (p) => p.geo_source === "nablus_mt" },
  { key: "palestinian", ar: "منتج فلسطيني أصيل", en: "Authentic Palestinian", icon: MapPin, match: (p) => p.country === "PS" },
  { key: "fresh", ar: "اكتشاف شيء جديد", en: "Something new", icon: Sparkles, match: (p) => (p.tags || []).includes("new") },
];

export default function OccasionDiscovery() {
  const { lang, t } = useLang();
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState("breakfast");

  const load = () => {
    setError(false);
    api.get("/products", { params: { limit: 60 } })
      .then((r) => setProducts(r.data))
      .catch(() => setError(true));
  };
  useEffect(() => { load(); }, []);

  const occ = OCCASIONS.find((o) => o.key === active);
  const filtered = useMemo(() => (products || []).filter(occ.match).slice(0, 4), [products, occ]);

  return (
    <section data-testid="occasion-discovery" className="mx-auto max-w-7xl px-6 py-20">
      <div className="mb-8 max-w-2xl">
        <span className="mb-2 inline-block rounded-full bg-olive/8 px-3 py-1 text-xs font-semibold text-olive">{t("occ_eyebrow")}</span>
        <h2 className="font-display text-3xl font-bold text-ink sm:text-4xl">{t("occ_title")}</h2>
        <p className="mt-3 text-base text-ink/55">{t("occ_desc")}</p>
      </div>

      <div className="mb-8 flex gap-2 overflow-x-auto pb-2" role="tablist">
        {OCCASIONS.map((o) => (
          <button
            key={o.key}
            data-testid={`occasion-chip-${o.key}`}
            onClick={() => setActive(o.key)}
            className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-300 ${
              active === o.key ? "border-olive bg-olive text-ivory shadow-md" : "border-olive/20 bg-white text-ink/70 hover:border-olive/50"
            }`}
          >
            <o.icon className="h-4 w-4" /> {lang === "en" ? o.en : o.ar}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded-3xl border border-dashed border-palred/30 py-16 text-center">
          <p className="text-ink/60">{t("error_generic")}</p>
          <button data-testid="occasion-retry" onClick={load} className="mt-4 rounded-full bg-olive px-6 py-2.5 text-sm font-semibold text-ivory">{t("retry")}</button>
        </div>
      ) : !products ? (
        <ProductGridSkeleton count={4} />
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-olive/20 py-16 text-center">
          <p className="text-ink/60">{t("occ_empty")}</p>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6"
          >
            {filtered.map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
          </motion.div>
        </AnimatePresence>
      )}
    </section>
  );
}
